import React, { useState, useMemo } from 'react';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Calculator, Percent, DollarSign, TrendingDown, Home, Wallet, Landmark, Building2, Shield, Printer } from 'lucide-react';
import { computeMI, LOAN_TYPES } from '../lib/mi';
import AmortizationSchedule from '../components/AmortizationSchedule';
import { useTabParam } from './shared/useTabParam';
import MoneyInput from './shared/MoneyInput';
import NumberInput from './shared/NumberInput';
import PickSelect from './shared/PickSelect';
import CalcSaveToContact from './shared/CalcSaveToContact';
import QualificationWizard from './qualification/QualificationWizard';
import WizardTaxLookupDialog from './qualification/WizardTaxLookupDialog';
import { formatDate } from './kit/time';

const fmt$ = (n) => (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt$$ = (n) => (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const calcPMT = (principal, rate, years) => {
  const r = rate / 100 / 12; const n = years * 12;
  if (r === 0) return principal / n;
  return (principal * r * Math.pow(1+r, n)) / (Math.pow(1+r, n) - 1);
};

const TERM_YEARS = [10, 15, 20, 25, 30];
const TERM_OPTIONS = TERM_YEARS.map((y) => ({ value: String(y), label: `${y} years` }));

function PaymentCalc() {
  const [loanType, setLoanType] = useState('conventional');
  const [price, setPrice] = useState(425000);
  const [down, setDown] = useState(10);
  const [rate, setRate] = useState(6.49);
  const [years, setYears] = useState(30);
  const [taxRate, setTaxRate] = useState(1.8);
  const [insurance, setInsurance] = useState(1500);
  const [hoa, setHoa] = useState(0);
  const [taxOpen, setTaxOpen] = useState(false);
  const [taxSource, setTaxSource] = useState(''); // where a looked-up tax rate came from; '' = typed or default

  const res = useMemo(() => {
    const baseLoan = price * (1 - down/100);
    const mi = computeMI({ loanType, loanAmount: baseLoan, propertyPrice: price, termYears: years });
    // Upfront fees (FHA UFMIP, VA funding fee, USDA guarantee fee) are typically financed
    const loan = mi.upfront_financed ? baseLoan + mi.upfront_fee : baseLoan;
    const pi = calcPMT(loan, rate, years);
    const tax = (price * taxRate/100) / 12;
    const ins = insurance / 12;
    const miM = mi.monthly;
    return { loan, baseLoan, pi, tax, ins, hoa, mi: miM, miInfo: mi, total: pi + tax + ins + hoa + miM };
  }, [loanType, price, down, rate, years, taxRate, insurance, hoa]);

  // Ticket 131 lookup (Texas Comptroller rates), same dialog the qualification wizard uses.
  const applyTaxLookup = ({ rate: looked, label }) => {
    setTaxRate(looked);
    setTaxSource(label);
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-5">
        <div>
          <Label className="text-sm font-semibold mb-2 block" style={{ color: '#070B5F' }}>Loan Program</Label>
          <div className="grid grid-cols-5 gap-2">
            {LOAN_TYPES.map(t => (
              <button key={t.key} onClick={() => setLoanType(t.key)} className={`py-2 rounded-lg text-xs font-semibold transition ${loanType === t.key ? 'text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} style={loanType === t.key ? { background: '#070B5F' } : {}}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <Field label="Home Price" value={price} min={50000} max={2000000} step={5000} onChange={setPrice} format={fmt$} icon={DollarSign} editable kind="money" />
        <Field label="Down Payment %" value={down} min={0} max={60} step={1} onChange={setDown} format={(v) => v + '%'} icon={Percent} editable suffix="%" />
        <Field label="Interest Rate" value={rate} min={2} max={12} step={0.5} onChange={setRate} format={(v) => v.toFixed(3) + '%'} icon={TrendingDown} editable suffix="%" />
        <PickSelect
          label="Term" value={String(years)} onChange={(v) => setYears(Number(v))} options={TERM_OPTIONS} testId="calc-term"
          labelClassName="text-sm font-semibold text-[#070B5F]"
        />
        <div className="pt-3 border-t border-slate-100 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Tax %/yr (estimate)</Label><NumberInput value={taxRate} onChange={(n) => { setTaxRate(n ?? 0); setTaxSource(''); }} className="mt-1" data-testid="calc-tax-rate" /></div>
            <div><Label className="text-xs">Insurance /yr (estimate)</Label><MoneyInput value={insurance} onChange={(n) => setInsurance(n ?? 0)} className="mt-1" data-testid="calc-insurance" /></div>
            <div><Label className="text-xs">HOA dues /mo</Label><MoneyInput value={hoa || null} onChange={(n) => setHoa(n ?? 0)} className="mt-1" data-testid="calc-hoa" /></div>
          </div>
          <div className="text-xs">
            <button type="button" onClick={() => setTaxOpen(true)} disabled={!(price > 0)}
              className="underline disabled:no-underline disabled:text-slate-400" style={price > 0 ? { color: '#070B5F' } : undefined}
              data-testid="calc-tax-lookup">
              Look up tax rate
            </button>
            <span className="ml-2 text-slate-400">{price > 0 ? 'Texas addresses only.' : 'Enter a home price first.'}</span>
            {taxSource && <div className="mt-1 text-slate-500" data-testid="calc-tax-source">Tax rate from lookup: {taxSource}</div>}
          </div>
        </div>

        {/* MI info panel */}
        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={14} style={{ color: '#6BA82E' }} />
            <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: '#6BA82E' }}>{res.miInfo.mi_type || 'No MI'}</span>
          </div>
          <div className="text-xs text-slate-600 leading-relaxed">{res.miInfo.notes}</div>
          {res.miInfo.upfront_fee > 0 && (
            <div className="mt-2 flex items-center justify-between text-xs bg-slate-50 rounded-lg p-2">
              <span className="text-slate-500">Upfront fee {res.miInfo.upfront_financed ? '(financed into loan)' : ''}</span>
              <span className="font-serif font-bold" style={{ color: '#070B5F' }}>{fmt$(res.miInfo.upfront_fee)}</span>
            </div>
          )}
        </div>
      </div>
      <div className="lg:col-span-2 rounded-2xl p-6 text-white shadow-lg" style={{ background: 'linear-gradient(160deg, #030524 0%, #070B5F 100%)' }}>
        <div className="text-xs uppercase tracking-widest text-white/60">Monthly Payment</div>
        <div className="font-serif text-5xl font-bold mt-2" style={{ color: '#A6E56C' }} data-testid="calc-total">{fmt$$(res.total)}</div>
        <div className="text-white/50 text-sm mt-1">Total loan: {fmt$(res.loan)} ({loanType.toUpperCase()})</div>
        <div className="mt-6 space-y-2 text-sm">
          <Row label="Principal & Interest" val={res.pi} />
          <Row label="Property Tax" val={res.tax} />
          <Row label="Home Insurance" val={res.ins} />
          <Row label="HOA Dues" val={res.hoa} />
          {res.mi > 0 && <Row label={res.miInfo.mi_type} val={res.mi} />}
        </div>
      </div>
      <div className="lg:col-span-5 print:hidden">
        <CalcSaveToContact title="Payment calculator" lines={[
          ['Loan program', loanType.toUpperCase()],
          ['Home price', fmt$(price)],
          ['Down payment', `${down}%`],
          ['Interest rate', `${rate}%`],
          ['Term', `${years} years`],
          ['Loan amount', fmt$(res.loan)],
          ['Property tax', `${taxRate}%/yr`],
          ['Insurance', `${fmt$(insurance)}/yr`],
          ['HOA dues', `${fmt$$(hoa)}/mo`],
          ['Monthly payment', fmt$$(res.total)],
        ]} />
      </div>
      <div className="lg:col-span-5">
        <AmortizationSchedule principal={res.loan} rate={rate} years={years} testId="portal-amort" />
      </div>
      <WizardTaxLookupDialog open={taxOpen} onOpenChange={setTaxOpen} purchasePrice={price} onApply={applyTaxLookup} />
    </div>
  );
}

function Row({ label, val }) {
  return <div className="flex justify-between"><span className="text-white/70">{label}</span><span className="font-semibold">{fmt$$(val)}</span></div>;
}
function Field({ label, value, min, max, step, onChange, format, icon: Icon, editable = false, suffix = '', kind = 'number' }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <Label className="text-sm font-semibold flex items-center gap-1.5" style={{ color: '#070B5F' }}>
          <Icon size={14} style={{ color: '#6BA82E' }} /> {label}
        </Label>
        {editable ? (
          <div className="flex items-center gap-1">
            {kind === 'money' ? (
              <MoneyInput
                value={value}
                onChange={(n) => onChange(n ?? 0)}
                className="w-28 text-right font-serif font-bold text-lg px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:border-[#6BA82E]"
                style={{ color: '#070B5F' }}
              />
            ) : (
              <NumberInput
                value={value}
                min={min}
                max={max}
                onChange={(n) => onChange(n ?? 0)}
                className="w-24 text-right font-serif font-bold text-lg px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:border-[#6BA82E]"
                style={{ color: '#070B5F' }}
              />
            )}
            {suffix && <span className="font-serif font-bold text-lg" style={{ color: '#070B5F' }}>{suffix}</span>}
          </div>
        ) : (
          <span className="font-serif font-bold" style={{ color: '#070B5F' }}>{format(value)}</span>
        )}
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} />
    </div>
  );
}

function DtiCalc() {
  const [income, setIncome] = useState(8000);
  const [housing, setHousing] = useState(2200);
  const [debts, setDebts] = useState(650);
  const front = income ? (housing/income*100).toFixed(1) : 0;
  const back = income ? ((housing+debts)/income*100).toFixed(1) : 0;
  const barColor = (n) => n < 36 ? '#22c55e' : n < 43 ? '#f59e0b' : '#ef4444';
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
        <div><Label>Gross Monthly Income</Label><MoneyInput value={income} onChange={(n) => setIncome(n ?? 0)} className="mt-1" /></div>
        <div><Label>Housing Payment (PITI)</Label><MoneyInput value={housing} onChange={(n) => setHousing(n ?? 0)} className="mt-1" /></div>
        <div><Label>Other Monthly Debts</Label><MoneyInput value={debts} onChange={(n) => setDebts(n ?? 0)} className="mt-1" /></div>
      </div>
      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500">Front-End DTI</div>
          <div className="text-4xl font-serif font-bold mt-2" style={{ color: barColor(front) }}>{front}%</div>
          <div className="h-2 mt-3 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${Math.min(front, 100)}%`, background: barColor(front) }} /></div>
          <div className="text-xs text-slate-500 mt-2">Guideline: FHA ≤31% • Conv ≤28%</div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500">Back-End DTI</div>
          <div className="text-4xl font-serif font-bold mt-2" style={{ color: barColor(back) }}>{back}%</div>
          <div className="h-2 mt-3 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${Math.min(back, 100)}%`, background: barColor(back) }} /></div>
          <div className="text-xs text-slate-500 mt-2">Guideline: Most programs ≤43% • QM up to 50%</div>
        </div>
        <CalcSaveToContact title="DTI calculator" lines={[
          ['Gross monthly income', fmt$$(income)],
          ['Housing payment (PITI)', fmt$$(housing)],
          ['Other monthly debts', fmt$$(debts)],
          ['Front-end DTI', `${front}%`],
          ['Back-end DTI', `${back}%`],
        ]} />
      </div>
    </div>
  );
}

function BuydownCalc() {
  const [loan, setLoan] = useState(400000);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(30);
  const p0 = calcPMT(loan, rate, years);
  const p1 = calcPMT(loan, rate-1, years);
  const p2 = calcPMT(loan, rate-2, years);
  const p3 = calcPMT(loan, rate-3, years);

  const cost321 = (p0-p3)*12 + (p0-p2)*12 + (p0-p1)*12;
  const cost21 = (p0-p2)*12 + (p0-p1)*12;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
        <div><Label>Loan Amount</Label><MoneyInput value={loan} onChange={(n) => setLoan(n ?? 0)} className="mt-1" /></div>
        <div><Label>Note Rate %</Label><NumberInput value={rate} onChange={(n) => setRate(n ?? 0)} className="mt-1" /></div>
        <div><Label>Term</Label><NumberInput integer value={years} onChange={(n) => setYears(n ?? 0)} className="mt-1" /></div>
      </div>
      <div className="space-y-3">
        <BuydownRow title="Year 1" rate={rate-3} pmt={p3} />
        <BuydownRow title="Year 2" rate={rate-2} pmt={p2} />
        <BuydownRow title="Year 3" rate={rate-1} pmt={p1} />
        <BuydownRow title="Year 4+" rate={rate} pmt={p0} />
        <div className="bg-white rounded-xl p-4 mt-2 border border-slate-100 flex justify-between">
          <div><div className="text-xs text-slate-500">3-2-1 Buydown Cost</div><div className="font-serif font-bold text-xl" style={{ color: '#070B5F' }}>{fmt$(cost321)}</div></div>
          <div><div className="text-xs text-slate-500">2-1 Buydown Cost</div><div className="font-serif font-bold text-xl" style={{ color: '#070B5F' }}>{fmt$(cost21)}</div></div>
        </div>
        <CalcSaveToContact title="Buydown calculator" lines={[
          ['Loan amount', fmt$(loan)],
          ['Note rate', `${rate}%`],
          ['Term', `${years} years`],
          ['Year 1', `${(rate - 3).toFixed(2)}% · ${fmt$$(p3)}/mo`],
          ['Year 2', `${(rate - 2).toFixed(2)}% · ${fmt$$(p2)}/mo`],
          ['Year 3', `${(rate - 1).toFixed(2)}% · ${fmt$$(p1)}/mo`],
          ['Year 4+', `${rate.toFixed(2)}% · ${fmt$$(p0)}/mo`],
          ['3-2-1 buydown cost', fmt$(cost321)],
          ['2-1 buydown cost', fmt$(cost21)],
        ]} />
      </div>
    </div>
  );
}
function BuydownRow({ title, rate, pmt }) {
  return <div className="bg-white rounded-xl p-3 border border-slate-100 flex items-center justify-between">
    <div className="text-sm font-semibold" style={{ color: '#070B5F' }}>{title}</div>
    <div className="text-sm text-slate-600">{rate.toFixed(2)}%</div>
    <div className="font-serif font-bold" style={{ color: '#6BA82E' }}>{fmt$$(pmt)}/mo</div>
  </div>;
}

function DSCRCalc() {
  const [rent, setRent] = useState(3200);
  const [piti, setPiti] = useState(2400);
  const dscr = piti ? (rent / piti).toFixed(2) : 0;
  const color = dscr >= 1.25 ? '#22c55e' : dscr >= 1 ? '#f59e0b' : '#ef4444';
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
        <div><Label>Monthly Rent</Label><MoneyInput value={rent} onChange={(n) => setRent(n ?? 0)} className="mt-1" /></div>
        <div><Label>Monthly PITI (with tax, ins, HOA)</Label><MoneyInput value={piti} onChange={(n) => setPiti(n ?? 0)} className="mt-1" /></div>
      </div>
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm text-center">
        <div className="text-xs uppercase tracking-widest text-slate-500">DSCR</div>
        <div className="font-serif text-6xl font-bold mt-2" style={{ color }}>{dscr}</div>
        <div className="text-slate-500 text-sm mt-3">Most lenders require ≥ 1.00. Best pricing at 1.25+.</div>
      </div>
      <div className="lg:col-span-2 print:hidden">
        <CalcSaveToContact title="DSCR calculator" lines={[
          ['Monthly rent', fmt$$(rent)],
          ['Monthly PITI', fmt$$(piti)],
          ['DSCR', String(dscr)],
        ]} />
      </div>
    </div>
  );
}

const CALC_TABS = ['payment', 'afford', 'dti', 'buydown', 'dscr'];
const TAB_LABELS = { payment: 'Payment', afford: 'Affordability', dti: 'DTI', buydown: 'Buydown', dscr: 'DSCR' };

export default function CalculatorsPage() {
  const [tab, setTab] = useTabParam('payment', { valid: CALC_TABS });
  return (
    <div className="p-4 md:p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }}>Mortgage Calculators</h1>
          <p className="text-slate-500 mt-1">Instant payment, affordability and ratio scenarios. Print them or save one to a contact.</p>
          <p className="hidden print:block text-sm text-slate-500 mt-1" data-testid="calc-print-caption">
            {TAB_LABELS[tab]} calculator · {formatDate(new Date().toISOString())}
          </p>
        </div>
        {/* Browser print: the portal header and these controls drop out, so "Save as PDF" is the sheet to hand a borrower. */}
        <button type="button" onClick={() => window.print()} className="btn-outline shrink-0 print:hidden" data-testid="calc-print">
          <Printer size={14} /> Print / Save as PDF
        </button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 h-auto mb-6 print:hidden">
          <TabsTrigger value="payment" className="py-2.5"><Home size={14} className="mr-2" />Payment</TabsTrigger>
          <TabsTrigger value="afford" className="py-2.5"><Wallet size={14} className="mr-2" />Affordability</TabsTrigger>
          <TabsTrigger value="dti" className="py-2.5"><Percent size={14} className="mr-2" />DTI</TabsTrigger>
          <TabsTrigger value="buydown" className="py-2.5"><TrendingDown size={14} className="mr-2" />Buydown</TabsTrigger>
          <TabsTrigger value="dscr" className="py-2.5"><Building2 size={14} className="mr-2" />DSCR</TabsTrigger>
        </TabsList>
        <TabsContent value="payment"><PaymentCalc /></TabsContent>
        <TabsContent value="afford"><QualificationWizard standalone /></TabsContent>
        <TabsContent value="dti"><DtiCalc /></TabsContent>
        <TabsContent value="buydown"><BuydownCalc /></TabsContent>
        <TabsContent value="dscr"><DSCRCalc /></TabsContent>
      </Tabs>
    </div>
  );
}
